import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AlarmMessage } from '../alarm-message';
import { AlarmService, PaginationParams } from '../alarm.service';
import { isEqual } from 'lodash-es';
import { compareAsc, addMinutes, isAfter, isBefore } from 'date-fns/esm';
import { SidebarPanelComponent } from '../../sidebar/sidebar-panel/sidebar-panel.component';
import { LandingPageTile } from '../../shared/tiles/home-tile.model';
import { SidebarService } from '../../shared/sidebar/sidebar.service';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { _, parseToDate } from '../../shared/utils';
import { LinkingService } from '../../linking/linking.service';
import { StateService } from '../../shared/state/state.service';
import { MapElement } from '../../shared/map/map-element.model';
import { Point } from 'geojson';
import { AppMessage } from '../../shared/app-message/app-message';
import { DateFilters, FilterEvent, FilterProperties, Filters, TypeFilters } from '../../sidebar/filters/filters.model';
import { AuthService } from '../../auth/auth.service';
import { WebsocketService } from '../../shared/websocket/websocket.service';
import { Subject, Observable, interval } from 'rxjs';
import { takeUntil, map, startWith, concatMap, debounceTime } from 'rxjs/operators';
import { AppStabilityService } from '../../shared/app-stability.service';

import { MapService } from '../../shared/map/map.service';

@Component({
  selector: 'qm-alarm-list',
  templateUrl: './alarm-list.component.html',
  styleUrls: ['./alarm-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlarmListComponent extends SidebarPanelComponent implements OnInit, OnDestroy {

  public alarmList: { data: AlarmMessage[], total: number } = {
    data: [],
    total: 0
  };
  public filters: Filters[] = [
    {
      id: 1,
      name: _('Status'),
      type: 'dropdown-single',
      noFiltersShowsAll: false,
      filter_values: [
        {
          name: _('Actives'),
          value: 'active',
          boolean: true
        },
        {
          name: _('Inactives'),
          value: 'inactive',
          boolean: false
        }
      ]
    }
  ];
  public paginationValues: PaginationParams = {
    page: 1,
    offset: 0,
    limit: 200,
    alarmTypes: [],
    active: true,
    trainTypes: []
  };
  public alarmFilters: Map<string, FilterProperties[] | TypeFilters> = new Map();

  private alarms: AlarmMessage[];
  public active: AlarmMessage;
  private noMarkerId: string;

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();

  /**
   * Emits the current time as Date once a second
   */
  public time$ = interval(1000).pipe(startWith(new Date().getTime()), map(() => new Date().getTime()));
  public currentTenantId: number;

  public initialDateRange: DateFilters = getInitialDate();

  private filtersChangedSubject = new Subject<FilterEvent>();
  private filtersChanged$ = this.filtersChangedSubject.pipe(
    takeUntil(this.ngUnsubscribe),
    debounceTime(750)
  );

  constructor(
    private changeDetector: ChangeDetectorRef,
    private alarmService: AlarmService,
    private websocket: WebsocketService,
    private mapService: MapService,
    private state: StateService,
    private authService: AuthService,
    private readonly linkingService: LinkingService,
    protected sidebarService: SidebarService,
    protected appMessageService: AppMessageService,
    private appStability: AppStabilityService
  ) {
    super(appMessageService, sidebarService);
    this.componentName = AlarmListComponent.getComponentName();
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Alarm list');
    tile.icon = 'far fa-bell';
    tile.title = _('Alarm list');
    tile.panel = AlarmListComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'alarm-list';
  }

  ngOnInit() {
    this.currentTenantId = this.authService.getTenant();

    this.prepareFilters();
    const init = () => {
      this.websocket.newWebsocketMessage$.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((data) => {
          if ((data.target.includes('Alarms') || data.target.includes('alarms')) && this.paginationValues.page === 1) {
            this.alarmService.PaginatedAlarmList(this.paginationValues);
          }
        });
      this.alarmService.paginatedAlarms$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
        if (result.data) {
          this.alarmList = result;
          this.alarmList.data.sort((a, b) => {
            return compareAsc(b.timestamp, a.timestamp);
          });
          this.changeDetector.detectChanges();
        }
      });
    };
    this.appStability.whenFirstStable().then(() => {
      console.log('App is stable, initializing alarm-list component');
      init();
    });
    this.filtersChanged$
      .subscribe((event) => {
        this.alarmList.data = [];
        if (event.dateFilters) {
          this.paginationValues.dates = event.dateFilters;
        }
        for (const s of event.customFilters) {
          if (s.id === 1) {
            this.paginationValues.active = s.filter_values[0].boolean;
          }
          this.alarmFilters.set(s.name, s.filter_values);
          const selectedTypes = this.filters[1].filter_values
            .filter(filter => filter.boolean ? filter.value : null)
            .map(filter => filter.value);
          this.paginationValues.alarmTypes = selectedTypes;
          const selectedTrainTypes = this.filters.find(f => f.id === 6)?.filter_values
            .filter(filter => filter.boolean ? filter.value : null)
            .map(filter => filter.value);
          this.paginationValues.trainTypes = selectedTrainTypes;
        }
        this.alarmService.PaginatedAlarmList(this.paginationValues);
        this.saveFilterState(event.customFilters);
      });
  }
  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public turnPage($event: any) {
    this.alarmList.data = [];
    if ($event > this.paginationValues.page) {
      this.paginationValues.offset = this.paginationValues.offset + 200;
      this.paginationValues.page = $event;
    } else {
      this.paginationValues.offset = this.paginationValues.offset - 200;
      this.paginationValues.page = $event;
    }
    this.alarmService.PaginatedAlarmList(this.paginationValues);
    this.changeDetector.markForCheck();
  }

  public filtersChanged($event: FilterEvent) {
    if ($event) {
      this.filtersChangedSubject.next($event);
    }
  }

  private saveFilterState(filters: any) {
    this.state.updateState('alarmList_filters', filters);
    this.changeDetector.detectChanges();
  }

  public isAlarmNew(time: Date, currentTime: number): boolean {
    return isBefore(currentTime, addMinutes(time, 15));
  }

  public getRowClass(severity: string, time: Date | string | number, currentTime: number): string {
    if (typeof time === 'string') {
      time = parseToDate(time);
    }
    const today = new Date(currentTime);
    const newTime = addMinutes(time, 10);
    let className = '';
    if (isAfter(newTime, today)) {
      const two = addMinutes(time, 2);
      const four = addMinutes(time, 4);
      const six = addMinutes(time, 6);
      const eight = addMinutes(time, 8);
      if (isBefore(today, two)) {
        className = 'fresh ';
      } else if (isBefore(today, four)) {
        className = 'pretty-fresh ';
      } else if (isBefore(today, six)) {
        className = 'old ';
      } else if (isBefore(today, eight)) {
        className = 'ancient ';
      }
    }

    if (severity === 'danger') {
      className += 'alarm-danger';
    } else if (severity === 'warning') {
      className += 'alarm-warning';
    } else {
      className += 'alarm-info';
    }
    return className;
  }

  public isActive = (alarm: AlarmMessage, activeAlarm: AlarmMessage): boolean => {
    return alarm && activeAlarm // Both must be defined to compare
      && alarm.metadata._id === activeAlarm.metadata._id;
  }

  public setActive(alarm: AlarmMessage): void {
    this.active = alarm;
  }

  private getIdentifier(alarm: AlarmMessage) {
    return AlarmService.getIdentifier(alarm.metadata);
  }

  public getExtras(alarm: AlarmMessage): string[] {
    switch (alarm.metadata.type) {
      case 'miproMeasure':
        return [alarm.metadata.payload.p_deviceid];
      case 'valmetDNA':
        return [alarm.metadata.payload.Tag, alarm.metadata.payload.EventAreaDescription];
    }
  }

  hasMarker = (alarm: AlarmMessage): Observable<boolean> => {
    const id = this.getIdentifier(alarm);
    return this.mapService.visibleMarkers$.pipe(
      startWith(null),
      takeUntil(this.ngUnsubscribe),
      concatMap(async () => {
        const marker = await this.getMarkerElement(id);
        return !!marker;
      })
    );
  }

  private async getMarkerElement(id: any): Promise<MapElement<Point>> {
    for (const linking of await this.linkingService.getLinkingsForSourceId(id)) {
      const marker = this.mapService.getMarker(linking.regoId);
      if (marker) {
        return marker;
      }
    }
    return this.mapService.getMarker(id);
  }

  private async prepareFilters() {
    const tenantFilters = this.authService.getTenantConfig().alarmFilters;
    if (tenantFilters) {
      this.filters.push(...tenantFilters);
    }

    // Load "boolean" for each filter_values element from localStorage,
    // while ignoring any other data in the saved object.
    const savedFilterData: Filters[] = this.state.getPartOfState('alarmList_filters');
    for (const savedFilter of (savedFilterData || [])) {
      const currentFilter = this.filters.find(filter => filter.id === savedFilter.id);
      for (const savedValue of (savedFilter.filter_values || [])) {
        const currentValue = currentFilter
          && currentFilter.filter_values
          && currentFilter.filter_values.find(filterValue => filterValue.value === savedValue.value);
        if (currentValue) {
          currentValue.boolean = savedValue.boolean;
        }
      }
    }

    this.paginationValues.active = this.filters[0]?.filter_values[0].boolean;
    const types = this.filters[1]?.filter_values
      .filter(o => o.boolean ? o.value : null)
      .map(o => o.value);
    this.paginationValues.alarmTypes = types;
    this.paginationValues.trainTypes = this.filters
      .find(f => f.id === 6)?.filter_values
      .filter(o => o.boolean ? o.value : null)
      .map(o => o.value);
    this.paginationValues.dates = this.initialDateRange;
    this.alarmService.PaginatedAlarmList(this.paginationValues);
  }

  public removeAlarmFromUI(alarmToRemove: AlarmMessage) {
    this.alarmList.data = this.alarmList.data.filter(alarm => alarm !== alarmToRemove);
    this.changeDetector.detectChanges();
  }

}

const getInitialDate = (): DateFilters => {
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  return {
    startDate,
    endDate,
    selected_type: 'Day'
  };
};
